import { Game } from './game/state';
import {
  DEFAULT_SETTINGS,
  clearGame,
  loadGame,
  loadSettings,
  loadStats,
  recordStart,
  recordWin,
  resetStats,
  saveGame,
  saveSettings,
  type Settings,
  type ThemeSetting,
} from './game/storage';
import { prefetch, requestPuzzle } from './game/puzzle-source';
import { DIFFICULTY_LABEL } from './sudoku/generator';
import { applyStep, createContext, findNextStep, TECHNIQUE_LABEL } from './sudoku/techniques';
import { DIFFICULTIES, type Difficulty } from './sudoku/types';
import { BoardView, type HintHighlight } from './ui/board';
import { KeypadView } from './ui/keypad';
import { close, closeAll, dialogButton, isDialogOpen, openDialog } from './ui/dialog';
import { byId, el, formatTime, toast, vibrate } from './ui/dom';

interface PendingHint {
  index: number;
  digit: number;
}

export class App {
  private game: Game;
  private settings: Settings = { ...DEFAULT_SETTINGS };
  private readonly board: BoardView;
  private readonly keypad: KeypadView;

  private selected: number | null = null;
  private noteMode = false;
  private paused = false;
  private hint: HintHighlight | null = null;
  private pendingHint: PendingHint | null = null;

  private timerHandle: number | undefined;
  private lastTick = 0;

  private constructor(game: Game) {
    this.game = game;
    this.board = new BoardView(byId('board'), (index) => this.select(index));
    this.keypad = new KeypadView(byId('keypad'), (digit) => this.enterDigit(digit));
  }

  static async start(): Promise<App> {
    // App-Shortcuts (?level=hard) starten direkt eine neue Partie.
    const requested = App.requestedDifficulty();
    const saved = requested ? null : loadGame();
    const game = saved ? Game.fromSnapshot(saved) : await App.createGame(requested ?? 'easy');
    const app = new App(game);
    app.settings = loadSettings();
    app.applyTheme();
    app.bindControls();
    app.bindKeyboard();
    app.bindLifecycle();
    app.startTimer();
    app.update();
    if (!saved) {
      recordStart(game.difficulty);
      // Sofort sichern: Sonst wäre ein frisches Rätsel weg, falls die App vor
      // dem ersten Zug geschlossen wird.
      app.save();
    }
    void prefetch(game.difficulty);
    return app;
  }

  /** Schwierigkeitsgrad aus der URL (App-Shortcut), danach wird sie bereinigt. */
  private static requestedDifficulty(): Difficulty | null {
    const level = new URLSearchParams(location.search).get('level');
    if (!level || !(DIFFICULTIES as readonly string[]).includes(level)) return null;
    history.replaceState(null, '', location.pathname);
    return level as Difficulty;
  }

  private static async createGame(difficulty: Difficulty): Promise<Game> {
    const puzzle = await requestPuzzle(difficulty);
    return new Game(puzzle.puzzle, puzzle.solution, puzzle.difficulty, puzzle.seed);
  }

  // -- Steuerung -----------------------------------------------------------

  private bindControls(): void {
    byId('menu-button').addEventListener('click', () => this.openMenu());
    byId('pause-button').addEventListener('click', () => this.togglePause());
    byId('resume-button').addEventListener('click', () => this.setPaused(false));
    byId('undo-button').addEventListener('click', () => this.undo());
    byId('erase-button').addEventListener('click', () => this.erase());
    byId('notes-button').addEventListener('click', () => this.toggleNoteMode());
    byId('hint-button').addEventListener('click', () => this.useHint());
  }

  private bindKeyboard(): void {
    document.addEventListener('keydown', (event) => {
      if (isDialogOpen() || event.metaKey || event.ctrlKey) return;

      if (event.key >= '1' && event.key <= '9') {
        this.enterDigit(Number(event.key));
        event.preventDefault();
        return;
      }

      switch (event.key) {
        case 'Backspace':
        case 'Delete':
        case '0':
          this.erase();
          break;
        case 'ArrowUp':
          this.move(0, -1);
          break;
        case 'ArrowDown':
          this.move(0, 1);
          break;
        case 'ArrowLeft':
          this.move(-1, 0);
          break;
        case 'ArrowRight':
          this.move(1, 0);
          break;
        case 'n':
        case 'N':
          this.toggleNoteMode();
          break;
        case 'h':
        case 'H':
          this.useHint();
          break;
        case 'u':
        case 'U':
          this.undo();
          break;
        case 'r':
        case 'R':
          this.redo();
          break;
        default:
          return;
      }
      event.preventDefault();
    });
  }

  private bindLifecycle(): void {
    // Beim Wechsel in den Hintergrund speichern und die Uhr anhalten.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.tick();
        this.save();
      } else {
        this.lastTick = performance.now();
      }
    });
    window.addEventListener('pagehide', () => {
      this.tick();
      this.save();
    });
  }

  private move(dx: number, dy: number): void {
    const current = this.selected ?? 0;
    const row = Math.min(8, Math.max(0, Math.floor(current / 9) + dy));
    const col = Math.min(8, Math.max(0, (current % 9) + dx));
    this.select(row * 9 + col);
    this.board.focus(row * 9 + col);
  }

  private select(index: number): void {
    if (this.paused) return;
    // Erneutes Tippen hebt die Auswahl bewusst nicht auf – sonst gehen
    // Eingaben verloren, wenn man dasselbe Feld zweimal antippt.
    if (this.selected === index) return;
    this.selected = index;
    this.clearHint();
    this.update();
  }

  // -- Eingaben ------------------------------------------------------------

  private enterDigit(digit: number): void {
    if (this.paused || this.game.completed) return;
    const index = this.selected;
    if (index === null) {
      toast('Zuerst ein Feld auswählen.');
      return;
    }
    if (this.game.isGiven(index)) return;

    this.clearHint();

    if (this.noteMode) {
      if (this.game.toggleNote(index, digit)) this.afterMove();
      return;
    }

    const result = this.game.setValue(index, digit);
    if (!result.changed) return;

    if (result.wrong) {
      if (this.settings.haptics) vibrate(60);
      if (this.settings.checkMistakes) this.board.flash(index);
    }
    this.afterMove();
    this.checkCompletion();
  }

  private erase(): void {
    if (this.paused || this.selected === null) return;
    if (this.game.clear(this.selected)) {
      this.clearHint();
      this.afterMove();
    }
  }

  private undo(): void {
    const touched = this.game.undo();
    if (touched.length === 0) return;
    this.selected = touched[0] ?? this.selected;
    this.clearHint();
    this.afterMove();
  }

  private redo(): void {
    const touched = this.game.redo();
    if (touched.length === 0) return;
    this.selected = touched[0] ?? this.selected;
    this.clearHint();
    this.afterMove();
  }

  private toggleNoteMode(): void {
    this.noteMode = !this.noteMode;
    this.update();
  }

  private afterMove(): void {
    this.save();
    this.update();
  }

  // -- Tipps ---------------------------------------------------------------

  private clearHint(): void {
    this.hint = null;
    this.pendingHint = null;
  }

  /**
   * Zwei Stufen: Der erste Druck erklärt den nächsten Schritt und hebt die
   * beteiligten Felder hervor, der zweite trägt die Ziffer ein.
   */
  private useHint(): void {
    if (this.paused || this.game.completed) return;

    // Falsche Eingaben zuerst: alles Weitere wäre sonst wertlos.
    for (let i = 0; i < 81; i++) {
      if (this.game.isWrong(i)) {
        this.selected = i;
        this.hint = { target: i, because: [] };
        this.game.hintsUsed++;
        this.board.flash(i);
        toast(`Feld R${Math.floor(i / 9) + 1}C${(i % 9) + 1} passt nicht zur Lösung.`);
        this.afterMove();
        return;
      }
    }

    const suggestion = this.findHint();
    if (!suggestion) {
      toast('Hier hilft nur Ausprobieren – dieses Rätsel ist gerade nicht logisch weiterzuführen.');
      return;
    }

    const { index, digit, text, because } = suggestion;

    if (this.pendingHint && this.pendingHint.index === index && this.pendingHint.digit === digit) {
      // Zweiter Druck: Ziffer eintragen.
      this.selected = index;
      this.game.setValue(index, digit);
      this.game.hintsUsed++;
      this.clearHint();
      this.board.flash(index);
      this.afterMove();
      this.checkCompletion();
      return;
    }

    this.selected = index;
    this.pendingHint = { index, digit };
    this.hint = { target: index, because };
    this.update();
    toast(`${text} (Tipp erneut drücken zum Eintragen)`);
  }

  /** Sucht den nächsten Schritt, der tatsächlich eine Ziffer setzt. */
  private findHint(): { index: number; digit: number; text: string; because: number[] } | null {
    const ctx = createContext(this.game.values);
    for (let guard = 0; guard < 200; guard++) {
      const step = findNextStep(ctx);
      if (!step) return null;
      if (step.placement) {
        return {
          index: step.placement.index,
          digit: step.placement.digit,
          text: `${TECHNIQUE_LABEL[step.technique]}: ${step.text}`,
          because: step.because,
        };
      }
      applyStep(ctx, step);
    }
    return null;
  }

  // -- Pause und Abschluss --------------------------------------------------

  private togglePause(): void {
    this.setPaused(!this.paused);
  }

  private setPaused(paused: boolean): void {
    if (this.game.completed) return;
    this.tick();
    this.paused = paused;
    this.save();
    this.update();
  }

  private checkCompletion(): void {
    if (!this.game.isSolved() || this.game.completed) return;
    this.tick();
    this.game.completed = true;
    this.save();
    this.update();
    const stats = recordWin(this.game.difficulty, this.game.elapsedMs, this.game.mistakes, this.game.hintsUsed);
    if (this.settings.haptics) vibrate([40, 60, 120]);
    this.showResult(stats.currentStreak);
  }

  private showResult(streak: number): void {
    const grid = el('div', { class: 'result-grid' });
    const stat = (value: string, label: string) =>
      el('div', {}, [el('strong', {}, [value]), el('span', {}, [label])]);
    grid.append(
      stat(formatTime(this.game.elapsedMs), 'Zeit'),
      stat(String(this.game.mistakes), 'Fehler'),
      stat(String(this.game.hintsUsed), this.game.hintsUsed === 1 ? 'Tipp' : 'Tipps'),
    );

    openDialog({
      title: 'Gelöst!',
      lead: `${DIFFICULTY_LABEL[this.game.difficulty]} · ${streak} in Folge`,
      dismissable: false,
      content: [
        grid,
        dialogButton('Noch eins', () => {
          close();
          void this.newGame(this.game.difficulty);
        }, { primary: true }),
        dialogButton('Schwierigkeit wählen', () => {
          close();
          this.openNewGameDialog();
        }),
        dialogButton('Gitter ansehen', () => close()),
      ],
    });
  }

  // -- Neues Spiel ----------------------------------------------------------

  private async newGame(difficulty: Difficulty): Promise<void> {
    toast('Neues Rätsel wird erzeugt …', 1200);
    const game = await App.createGame(difficulty);
    this.game = game;
    this.selected = null;
    this.paused = false;
    this.clearHint();
    recordStart(difficulty);
    this.save();
    this.update();
    toast(`${DIFFICULTY_LABEL[game.difficulty]} – viel Erfolg!`, 1400);
  }

  // -- Dialoge --------------------------------------------------------------

  private openMenu(): void {
    openDialog({
      title: 'Menü',
      content: [
        dialogButton('Neues Spiel', () => {
          close();
          this.openNewGameDialog();
        }, { primary: true }),
        dialogButton('Notizen automatisch füllen', () => {
          close();
          this.game.fillNotes();
          this.afterMove();
          toast('Alle möglichen Ziffern als Notizen eingetragen.');
        }, { meta: 'Kandidaten eintragen' }),
        dialogButton('Statistik', () => {
          close();
          this.openStatsDialog();
        }),
        dialogButton('Einstellungen', () => {
          close();
          this.openSettingsDialog();
        }),
        dialogButton('Spielregeln', () => {
          close();
          this.openRulesDialog();
        }),
      ],
    });
  }

  private openNewGameDialog(): void {
    const stats = loadStats();
    const buttons = DIFFICULTIES.map((difficulty) => {
      const best = stats.perDifficulty[difficulty].bestMs;
      return dialogButton(
        DIFFICULTY_LABEL[difficulty],
        () => {
          close();
          void this.newGame(difficulty);
        },
        { meta: best === null ? 'noch keine Bestzeit' : `Bestzeit ${formatTime(best)}` },
      );
    });

    openDialog({
      title: 'Neues Spiel',
      lead: this.game.completed ? undefined : 'Die laufende Partie geht dabei verloren.',
      content: buttons,
    });
  }

  private openStatsDialog(): void {
    const stats = loadStats();
    const table = el('table', { class: 'stats-table' });
    const head = el('tr', {}, [
      el('th', {}, ['Grad']),
      el('th', {}, ['Gespielt']),
      el('th', {}, ['Gelöst']),
      el('th', {}, ['Bestzeit']),
    ]);
    table.append(el('thead', {}, [head]));

    const body = el('tbody');
    for (const difficulty of DIFFICULTIES) {
      const entry = stats.perDifficulty[difficulty];
      body.append(
        el('tr', {}, [
          el('td', {}, [DIFFICULTY_LABEL[difficulty]]),
          el('td', {}, [String(entry.started)]),
          el('td', {}, [String(entry.won)]),
          el('td', {}, [entry.bestMs === null ? '–' : formatTime(entry.bestMs)]),
        ]),
      );
    }
    table.append(body);

    openDialog({
      title: 'Statistik',
      lead: `Aktuelle Serie: ${stats.currentStreak} · Beste Serie: ${stats.bestStreak}`,
      content: [
        table,
        el('section', {}, [
          dialogButton('Statistik zurücksetzen', () => {
            resetStats();
            close();
            toast('Statistik zurückgesetzt.');
          }),
        ]),
      ],
    });
  }

  private openSettingsDialog(): void {
    const themes: { value: ThemeSetting; label: string }[] = [
      { value: 'system', label: 'System' },
      { value: 'light', label: 'Hell' },
      { value: 'dark', label: 'Dunkel' },
    ];

    const segmented = el('div', { class: 'segmented', role: 'group', 'aria-label': 'Darstellung' });
    for (const theme of themes) {
      const button = el('button', { type: 'button', 'aria-pressed': String(this.settings.theme === theme.value) }, [
        theme.label,
      ]);
      button.addEventListener('click', () => {
        this.settings.theme = theme.value;
        saveSettings(this.settings);
        this.applyTheme();
        for (const sibling of segmented.children) {
          sibling.setAttribute('aria-pressed', String(sibling === button));
        }
      });
      segmented.append(button);
    }

    const toggles: { key: keyof Settings; label: string }[] = [
      { key: 'checkMistakes', label: 'Fehler sofort markieren' },
      { key: 'highlightConflicts', label: 'Zeile, Spalte und Block hervorheben' },
      { key: 'highlightSameDigit', label: 'Gleiche Ziffern hervorheben' },
      { key: 'showTimer', label: 'Zeit anzeigen' },
      { key: 'haptics', label: 'Vibration bei Fehleingabe' },
    ];

    const switches = el('section');
    for (const toggle of toggles) {
      const input = el('input', { type: 'checkbox', id: `setting-${toggle.key}` });
      input.checked = Boolean(this.settings[toggle.key]);
      input.addEventListener('change', () => {
        (this.settings as unknown as Record<string, boolean>)[toggle.key] = input.checked;
        saveSettings(this.settings);
        this.update();
      });
      switches.append(
        el('label', { class: 'switch', for: `setting-${toggle.key}` }, [el('span', {}, [toggle.label]), input]),
      );
    }

    openDialog({
      title: 'Einstellungen',
      content: [el('section', {}, [el('h3', {}, ['Darstellung']), segmented]), switches],
    });
  }

  private openRulesDialog(): void {
    const list = el('ul');
    for (const rule of [
      'Jede Zeile enthält die Ziffern 1 bis 9 genau einmal.',
      'Jede Spalte enthält die Ziffern 1 bis 9 genau einmal.',
      'Jeder der neun 3×3-Blöcke enthält die Ziffern 1 bis 9 genau einmal.',
      'Jedes Rätsel hat genau eine Lösung – Raten ist nie nötig.',
    ]) {
      list.append(el('li', {}, [rule]));
    }

    const tips = el('ul');
    for (const tip of [
      'Notizen: Stift antippen, dann Ziffern als Kandidaten eintragen.',
      'Tipp: Der erste Druck erklärt den nächsten Schritt, der zweite trägt ihn ein.',
      'Tastatur: Ziffern 1–9, Pfeiltasten, N für Notizen, U für Rückgängig.',
    ]) {
      tips.append(el('li', {}, [tip]));
    }

    openDialog({
      title: 'Spielregeln',
      content: [
        el('section', {}, [el('h3', {}, ['Regeln']), list]),
        el('section', {}, [el('h3', {}, ['Bedienung']), tips]),
      ],
    });
  }

  // -- Uhr ------------------------------------------------------------------

  private startTimer(): void {
    this.lastTick = performance.now();
    this.timerHandle = window.setInterval(() => {
      this.tick();
      this.renderStatus();
    }, 1000);
  }

  private tick(): void {
    const now = performance.now();
    const delta = now - this.lastTick;
    this.lastTick = now;
    if (this.paused || this.game.completed || document.hidden) return;
    this.game.elapsedMs += delta;
  }

  stop(): void {
    window.clearInterval(this.timerHandle);
  }

  // -- Darstellung ----------------------------------------------------------

  private applyTheme(): void {
    const root = document.documentElement;
    if (this.settings.theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', this.settings.theme);
  }

  private save(): void {
    saveGame(this.game.toSnapshot());
  }

  private update(): void {
    this.board.render(this.game, { selected: this.selected, hint: this.hint, hidden: this.paused }, this.settings);
    this.keypad.render(this.game, true);
    this.renderStatus();

    byId<HTMLButtonElement>('undo-button').disabled = !this.game.canUndo;
    const notesButton = byId<HTMLButtonElement>('notes-button');
    notesButton.setAttribute('aria-pressed', String(this.noteMode));
    byId('notes-state').textContent = this.noteMode ? 'An' : 'Aus';
    byId<HTMLButtonElement>('hint-button').disabled = this.game.completed;

    const veil = byId('board-veil');
    veil.hidden = !this.paused;
    byId('board-veil-text').textContent = 'Pausiert';
  }

  private renderStatus(): void {
    byId('difficulty-label').textContent = DIFFICULTY_LABEL[this.game.difficulty];

    const timer = byId('timer-label');
    timer.hidden = !this.settings.showTimer;
    timer.textContent = formatTime(this.game.elapsedMs);

    const mistakes = byId('mistakes-label');
    mistakes.hidden = this.game.mistakes === 0;
    mistakes.textContent = `Fehler ${this.game.mistakes}`;
    mistakes.classList.toggle('warn', this.game.mistakes > 0);
  }

  /** Verwirft die gespeicherte Partie und schließt alle Dialoge. */
  reset(): void {
    clearGame();
    closeAll();
  }
}
